"use client";

import type { RefObject } from "react";
import { useLayoutEffect, useRef } from "react";

/**
 * Creates a stable ref that always holds the latest version of a callback function.
 * Useful for avoiding stale closures in effects and callbacks while maintaining a stable reference.
 *
 * @param fn - The callback function to store in the ref
 * @returns A mutable ref object containing the latest callback
 *
 * @example
 * ```tsx
 * const onClick = () => console.log(someValue);
 * const onClickRef = useCallbackRef(onClick);
 *
 * useEffect(() => {
 *   // onClickRef.current always points to the latest onClick
 *   const handler = () => onClickRef.current();
 *   element.addEventListener('click', handler);
 *   return () => element.removeEventListener('click', handler);
 * }, []); // Safe to omit onClick from deps
 * ```
 */
/*
 * biome-ignore lint/suspicious/noExplicitAny: The templating type just needs
 * to ensure that a function is passed
 */
export function useCallbackRef<T extends (...args: any[]) => any>(fn: T): RefObject<T> {
  const callbackRef = useRef(fn);

  useLayoutEffect(() => {
    callbackRef.current = fn;
  });

  return callbackRef;
}
